import React, { ReactElement, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core";
import theme from "../../../themes";
import {
  LanguageCodeEnum,
  useUpdateLoggedInUserLanguageMutation,
} from "../../../graphql/types";

import "react-toastify/dist/ReactToastify.css";
import ChamberLoading from "../../common/ChamberLoading";

import { Radio, RadioGroup, FormControlLabel } from "@material-ui/core";
import LanguageIcon from "@material-ui/icons/Language";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import useGetLoggedInUserLanguageFromUserIdInAuth from "../../common/Hooks/useGetLoggedInUserLanguageFromUserIdInAuth";

import { useLocales } from "../../../locales/LocalizationContext";
import { useEnumStrings } from "../../enumStrings";

const useStyles = makeStyles((themes: any) => ({
  root: {
    backgroundColor: theme.palette.delete.main,
    color: theme.palette.delete.contrastText,
    toolbar: theme.mixins.toolbar,
  },
  background: {},
  title: {
    marginRight: "24px",
    marginLeft: "24px",
    fontSize: "30px",
    color: "white",
    fontWeight: "bold",
    marginBottom: "30px",
    marginTop: "15px",
    display: "flex",
  },
  titleContent: {
    display: "flex",
    flexDirection: "column-reverse",
    [theme.breakpoints.up("lg")]: {
      paddingTop: "30px",
    },
  },
  content: {
    display: "flex",
    flexDirection: "column",
    background: "#FFFFFF",
    flexGrow: 1,
    minHeight: "85vh",
    borderRadius: "16px",
    margin: "20px",
    padding: "40px 20px",
    [theme.breakpoints.down("sm")]: {
      margin: 0,
    },
    marginTop: 0,
    alignItems: "center",
  },
  card: {
    width: "100%",
    maxWidth: "560px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #E6E8EB",
    borderRadius: "20px",
    padding: "40px 32px",
    boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.04)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  iconCircle: {
    width: "72px",
    height: "72px",
    borderRadius: "50%",
    backgroundColor: "#FFF4DC",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "20px",
  },
  heading: {
    fontFamily: "Inter, sans-serif",
    fontSize: "22px",
    fontWeight: 700,
    color: "#24272C",
    margin: 0,
    lineHeight: "30px",
  },
  subheading: {
    fontFamily: "Inter, sans-serif",
    fontSize: "14px",
    fontWeight: 400,
    color: "#6B7280",
    margin: "8px 0 0 0",
    lineHeight: "20px",
  },
  divider: {
    width: "100%",
    height: "1px",
    background: "#EFF1F4",
    margin: "28px 0",
  },
  languageRadioGroup: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    gap: "16px",
    width: "100%",
    margin: 0,
    flexWrap: "wrap" as const,
  },
  languageOption: {
    flex: "1 1 180px",
    maxWidth: "220px",
    margin: 0,
    padding: "14px 18px",
    border: "1px solid #E6E8EB",
    borderRadius: "12px",
    cursor: "pointer",
    backgroundColor: "#FFFFFF",
    transition: "border-color 150ms ease, background-color 150ms ease",
    "&:hover": {
      borderColor: "#E1A731",
    },
    "& .MuiFormControlLabel-label": {
      fontFamily: "Inter, sans-serif",
      fontSize: "16px",
      fontWeight: 600,
      color: "#24272C",
    },
  },
  languageOptionSelected: {
    borderColor: "#E1A731",
    backgroundColor: "#FFF8EB",
  },
  radio: {
    color: "#E1A731 !important",
    padding: "4px 8px 4px 0",
  },
}));
// Define Yup validation schema
const validationSchema = Yup.object({
  language: Yup.string().required(""),
});

const GeneralSettings = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();
  const [initialLanguage, setInitialLanguage] = useState<string | null>("");
  const loggedInUserLanguageCode = useGetLoggedInUserLanguageFromUserIdInAuth();
  const [updateUserLanguage] = useUpdateLoggedInUserLanguageMutation();

  const { allLangs, onChangeLang, currentLang } = useLocales();

  const handleLanguageChange = async (languageCode: string) => {
    try {
      const selectedLanguage =
        languageCode === "en" ? LanguageCodeEnum.En : LanguageCodeEnum.Fr;

      onChangeLang(languageCode);
      await updateUserLanguage({
        variables: { languageCode: selectedLanguage },
        refetchQueries: ["getUser"],
      });

      setInitialLanguage(languageCode); // Update UI state dynamically
    } catch (error) {
      console.error("Error updating language:", error);
    }
  };

  useEffect(() => {
    // Set initial language from context if available, otherwise from backend
    if (currentLang) {
      setInitialLanguage(typeof currentLang === "string" ? currentLang : "en"); // Fallback to English if currentLang is not a string
    } else if (loggedInUserLanguageCode) {
      setInitialLanguage(loggedInUserLanguageCode.toLowerCase());
    }
  }, [loggedInUserLanguageCode, currentLang]);

  return (
    <span>
      <div className={classes.titleContent}>
        <div
          className={classes.title}
          style={{
            flexDirection: "column",
          }}
        >
          <span>{enumStrings.settings.settingTitle}</span>
          <span
            style={{
              marginLeft: "1px",
              fontWeight: "normal",
              fontSize: "16px",
            }}
          ></span>
        </div>
      </div>
      <div className={classes.content}>
        {!initialLanguage ? (
          <ChamberLoading />
        ) : (
          <Formik
            enableReinitialize
            initialValues={{ language: initialLanguage }}
            validationSchema={validationSchema}
            onSubmit={(values) => handleLanguageChange(values.language)}
          >
            {({ values, setFieldValue, handleSubmit }) => (
              <Form onSubmit={handleSubmit} style={{ width: "100%", display: "flex", justifyContent: "center" }}>
                <div className={classes.card}>
                  <div className={classes.iconCircle}>
                    <LanguageIcon style={{ color: "#E1A731", fontSize: "36px" }} />
                  </div>
                  <h2 className={classes.heading}>Choose language</h2>
                  <h2 className={classes.heading}>Choisir la langue</h2>
                  <p className={classes.subheading}>
                    Your selection saves automatically.
                  </p>

                  <div className={classes.divider} />

                  <RadioGroup
                    row
                    name="language"
                    className={classes.languageRadioGroup}
                    value={values.language}
                    onChange={(event) => {
                      const next = event.target.value;
                      setFieldValue("language", next);
                      handleLanguageChange(next);
                    }}
                  >
                    {allLangs.map((lang) => {
                      const isSelected = values.language === lang.value;
                      return (
                        <FormControlLabel
                          key={lang.label}
                          value={lang.value}
                          className={`${classes.languageOption} ${
                            isSelected ? classes.languageOptionSelected : ""
                          }`}
                          label={lang.label}
                          control={<Radio className={classes.radio} />}
                        />
                      );
                    })}
                  </RadioGroup>
                </div>
              </Form>
            )}
          </Formik>
        )}
      </div>
    </span>
  );
};

export default GeneralSettings;
